import { Phone, Mic, MicOff, PhoneOff, Sparkles, MessageSquare, AlertCircle, CheckCircle, Clock, Plus, Loader2, Trash2, Calendar, List } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { cn } from "@/lib/utils";
import { useCalls, useDeleteCall, useAnalyzeCall } from "@/hooks/useCalls";
import { LogCallDialog } from "@/components/calls/LogCallDialog";
import { CallsCalendar } from "@/components/calls/CallsCalendar";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { format, formatDistanceToNow } from "date-fns";
import { toast } from "sonner";
import { AIQuickActions } from "@/components/ai/AIQuickActions";
import { AIVoiceCallLogger } from "@/components/ai/AIVoiceCallLogger";

const aiSuggestions = [
  "Mention the 20% discount for annual billing",
  "Ask about their current pain points with existing solution",
  "Offer a free trial extension if needed",
];

const objections = [
  {
    category: "Price",
    text: "Your solution is more expensive than competitors",
    response:
      "I understand price is important. Let me show you the ROI our customers typically see within 3 months...",
  },
  {
    category: "Timing",
    text: "We're not ready to make a decision right now",
    response:
      "I completely understand. Would it help if I scheduled a follow-up for next quarter?",
  },
];

const formatDuration = (seconds: number | null) => {
  if (!seconds) return "0:00";
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, "0")}`;
};

export default function Calls() {
  const [isInCall, setIsInCall] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [activeTab, setActiveTab] = useState<"list" | "calendar">("list");
  const { data: calls, isLoading } = useCalls();
  const deleteCall = useDeleteCall();
  const analyzeCall = useAnalyzeCall();

  const handleAnalyzeCall = async (call: NonNullable<typeof calls>[0]) => {
    if (!call.transcript) {
      toast.error("No transcript available to analyze");
      return;
    }

    const contactName = call.contacts
      ? `${call.contacts.first_name} ${call.contacts.last_name}`
      : call.leads
      ? `${call.leads.first_name} ${call.leads.last_name}`
      : undefined;

    analyzeCall.mutate({
      callId: call.id,
      call: {
        transcript: call.transcript,
        duration_seconds: call.duration_seconds || 0,
        direction: call.direction,
        contact_name: contactName,
        deal_title: call.deals?.title,
      },
    });
  };

  const completedCalls = calls?.filter((c) => c.status === "completed") || [];
  
  // Calculate today's stats
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todaysCalls = completedCalls.filter(
    (c) => new Date(c.created_at) >= today
  );
  const totalMinutesToday = todaysCalls.reduce(
    (sum, c) => sum + (c.duration_seconds || 0),
    0
  ) / 60;
  const positiveCalls = todaysCalls.filter(
    (c) => c.ai_sentiment === "positive"
  ).length;

  if (isLoading) {
    return (
      <div className="space-y-6 animate-fade-in" data-wizard="calls">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">AI Call Assistant</h1>
            <p className="text-muted-foreground">Real-time guidance during your sales calls</p>
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <Skeleton className="h-96 w-full" />
          </div>
          <Skeleton className="h-96 w-full" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">AI Call Assistant</h1>
          <p className="text-muted-foreground">
            Real-time guidance during your sales calls
          </p>
        </div>
        <div className="flex items-center gap-3">
          <LogCallDialog />
          <Button
            className="gap-2 ai-glow"
            size="lg"
            onClick={() => setIsInCall(!isInCall)}
          >
            <Phone className="h-5 w-5" />
            Start AI-Assisted Call
          </Button>
        </div>
      </div>

      {/* View Toggle */}
      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as "list" | "calendar")}>
        <TabsList>
          <TabsTrigger value="list" className="gap-2">
            <List className="h-4 w-4" />
            List View
          </TabsTrigger>
          <TabsTrigger value="calendar" className="gap-2">
            <Calendar className="h-4 w-4" />
            Calendar View
          </TabsTrigger>
        </TabsList>

        <TabsContent value="calendar" className="mt-6">
          <CallsCalendar calls={calls || []} />
        </TabsContent>

        <TabsContent value="list" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Call Area */}
            <div className="lg:col-span-2 space-y-6">
              {/* Active Call Panel */}
              {isInCall ? (
                <div className="glass rounded-xl p-6 gradient-border">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-4">
                      <div className="h-16 w-16 rounded-full bg-primary/20 flex items-center justify-center animate-pulse">
                        <Phone className="h-8 w-8 text-primary" />
                      </div>
                      <div>
                        <h2 className="text-xl font-semibold">Active Call</h2>
                        <p className="text-muted-foreground">In Progress</p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="h-2 w-2 rounded-full bg-success animate-pulse" />
                          <span className="text-sm text-success">
                            Call in progress
                          </span>
                          <span className="text-sm text-muted-foreground">
                            • 02:34
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant={isMuted ? "destructive" : "outline"}
                        size="icon"
                        className="h-12 w-12 rounded-full"
                        onClick={() => setIsMuted(!isMuted)}
                      >
                        {isMuted ? (
                          <MicOff className="h-5 w-5" />
                        ) : (
                          <Mic className="h-5 w-5" />
                        )}
                      </Button>
                      <Button
                        variant="destructive"
                        size="icon"
                        className="h-12 w-12 rounded-full"
                        onClick={() => setIsInCall(false)}
                      >
                        <PhoneOff className="h-5 w-5" />
                      </Button>
                    </div>
                  </div>

                  {/* Live Transcript */}
                  <div className="bg-muted/50 rounded-lg p-4 h-48 overflow-y-auto mb-4">
                    <div className="space-y-3 text-sm">
                      <div>
                        <span className="text-muted-foreground">You:</span>{" "}
                        <span>
                          "Hi, thanks for taking the time to speak with me
                          today..."
                        </span>
                      </div>
                      <div>
                        <span className="text-primary">Contact:</span>{" "}
                        <span>
                          "Of course! I've been looking forward to learning more
                          about your platform..."
                        </span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">You:</span>{" "}
                        <span>
                          "Great! Based on our previous conversation, I understand
                          you're looking for..."
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* AI Suggestions */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm font-medium">
                      <Sparkles className="h-4 w-4 text-primary" />
                      <span>AI Suggestions</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {aiSuggestions.map((suggestion, i) => (
                        <button
                          key={i}
                          className="px-3 py-1.5 rounded-full text-sm bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
                        >
                          {suggestion}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="glass rounded-xl p-12 text-center">
                  <Phone className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
                  <h2 className="text-xl font-semibold mb-2">No active call</h2>
                  <p className="text-muted-foreground mb-6">
                    Start an AI-assisted call to get real-time suggestions and
                    objection handling
                  </p>
                  <Button className="gap-2" onClick={() => setIsInCall(true)}>
                    <Phone className="h-4 w-4" />
                    Start Call
                  </Button>
                </div>
              )}

              {/* Recent Calls */}
              <div className="glass rounded-xl p-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold">Recent Calls</h3>
                  <span className="text-sm text-muted-foreground">
                    {completedCalls.length} calls
                  </span>
                </div>
                <div className="space-y-4">
                  {completedCalls.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      No calls logged yet. Log your first call or start an AI-assisted call.
                    </div>
                  ) : (
                    completedCalls.slice(0, 10).map((call) => (
                      <div
                        key={call.id}
                        className="flex items-start gap-4 p-4 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors"
                      >
                        <div
                          className={cn(
                            "p-2 rounded-lg",
                            call.ai_sentiment === "positive"
                              ? "bg-success/10"
                              : call.ai_sentiment === "negative"
                              ? "bg-destructive/10"
                              : "bg-muted"
                          )}
                        >
                          {call.ai_sentiment === "positive" ? (
                            <CheckCircle className="h-5 w-5 text-success" />
                          ) : call.ai_sentiment === "negative" ? (
                            <AlertCircle className="h-5 w-5 text-destructive" />
                          ) : (
                            <MessageSquare className="h-5 w-5 text-muted-foreground" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-1">
                            <div>
                              <span className="font-medium">
                                {call.contacts
                                  ? `${call.contacts.first_name} ${call.contacts.last_name}`
                                  : call.leads
                                  ? `${call.leads.first_name} ${call.leads.last_name}`
                                  : "Unknown Contact"}
                              </span>
                              {call.leads?.company_name && (
                                <span className="text-muted-foreground">
                                  {" "}
                                  • {call.leads.company_name}
                                </span>
                              )}
                            </div>
                            <span className="text-sm text-muted-foreground">
                              {formatDistanceToNow(new Date(call.created_at), { addSuffix: true })}
                            </span>
                          </div>
                          <p className="text-sm text-muted-foreground truncate">
                            {call.ai_summary || call.notes || "No summary available"}
                          </p>
                          <div className="flex items-center gap-4 mt-2">
                            <div className="flex items-center gap-1 text-xs text-muted-foreground">
                              <Clock className="h-3 w-3" />
                              <span>{formatDuration(call.duration_seconds)}</span>
                            </div>
                            {call.ai_sentiment && (
                              <span
                                className={cn(
                                  "text-xs px-2 py-0.5 rounded-full",
                                  call.ai_sentiment === "positive"
                                    ? "bg-success/10 text-success"
                                    : call.ai_sentiment === "negative"
                                    ? "bg-destructive/10 text-destructive"
                                    : "bg-muted text-muted-foreground"
                                )}
                              >
                                {call.ai_sentiment}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <AIQuickActions 
                            entityType="call" 
                            entity={call}
                          />
                          {call.transcript && !call.ai_summary && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="gap-1 text-primary"
                              onClick={() => handleAnalyzeCall(call)}
                              disabled={analyzeCall.isPending}
                            >
                              {analyzeCall.isPending ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                              ) : (
                                <Sparkles className="h-3 w-3" />
                              )}
                              Analyze
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-destructive hover:text-destructive"
                            onClick={() => deleteCall.mutate(call.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Voice Call Logger */}
              <AIVoiceCallLogger />

              {/* Objection Library */}
              <div className="glass rounded-xl p-5">
                <div className="flex items-center gap-2 mb-4">
                  <AlertCircle className="h-5 w-5 text-warning" />
                  <h3 className="font-semibold">Objection Handling</h3>
                </div>
                <div className="space-y-4">
                  {objections.map((objection, i) => (
                    <div
                      key={i}
                      className="p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors cursor-pointer"
                    >
                      <span className="stage-pill bg-warning/10 text-warning text-xs mb-2 inline-block">
                        {objection.category}
                      </span>
                      <p className="text-sm font-medium mb-2">"{objection.text}"</p>
                      <p className="text-xs text-muted-foreground">
                        {objection.response}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Call Stats */}
              <div className="glass rounded-xl p-5">
                <h3 className="font-semibold mb-4">Today's Stats</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-3 rounded-lg bg-muted/50">
                    <p className="text-2xl font-bold">{todaysCalls.length}</p>
                    <p className="text-xs text-muted-foreground">Calls Made</p>
                  </div>
                  <div className="text-center p-3 rounded-lg bg-muted/50">
                    <p className="text-2xl font-bold">{Math.floor(totalMinutesToday)}m</p>
                    <p className="text-xs text-muted-foreground">Total Time</p>
                  </div>
                  <div className="text-center p-3 rounded-lg bg-muted/50">
                    <p className="text-2xl font-bold text-success">{positiveCalls}</p>
                    <p className="text-xs text-muted-foreground">Positive</p>
                  </div>
                  <div className="text-center p-3 rounded-lg bg-muted/50">
                    <p className="text-2xl font-bold">{completedCalls.length}</p>
                    <p className="text-xs text-muted-foreground">Total Calls</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
